/**
 * AppComponent
 *
 * Root component of the application.
 * Manages main layout, header navigation toggle, SVG icons, and server connectivity status.
 *
 * Non-Angular dependencies:
 * - MenuCommonComponent (shared component)
 * - AlertComponent (shared component)
 * - SpinnerComponent (shared component)
 * - LoginService (@app/common/login.service)
 * - ServerStatusService (@app/common/server-status.service)
 */
import { Component, OnInit, ChangeDetectionStrategy, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';

import { MenuCommonComponent } from './shared/menu-common/menu-common.component';
import { AlertComponent } from './shared/alert/alert.component';
import { SpinnerComponent } from './shared/spinner/spinner.component';
import { LoginService } from '@app/common/login.service';
import { ServerStatusService } from '@app/common/server-status.service';
import { ServerInfoService } from '@app/common/server-info/server-info.service';
import { hrIsAdmin } from '@app/shared/_models/hr';

import { environment } from '@app/../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MenuCommonComponent, AlertComponent, SpinnerComponent, RouterOutlet, RouterLink, MatIconModule, AsyncPipe],
})
export class AppComponent implements OnInit {
  /** Controls visibility of top navigation bar */
  public showNavbar = true;

  /** Title of application */
  public title: string;

  /** Active navigation route URL */
  public currentUrl: string = '/';

  /** Application version retrieved from front package.json */
  public appVersion = environment.appVersion;

  /** FSE library version retrieved from ngws-fselib package.json */
  public fselibVersion = environment.fselibVersion;

  /** Version du serveur (GET /version, connecté seulement), relue à chaque connexion et retour en ligne : un redéploiement se voit sans recharger. */
  public readonly serverVersion = signal<string | null>(null);

  /** Habilitation Admin lue dans le jeton : confort d'écran pour le lien du pied de page, le serveur décide (403 sur /info). */
  public readonly isAdmin = signal(false);

  private readonly serverInfoService = inject(ServerInfoService);
  private readonly destroyRef = inject(DestroyRef);

  /** Stream indicating connection status to server */
  public isOnline$ = this.serverStatusService.isOnline$;

  constructor(
    private router: Router,
    public loginService: LoginService,
    private serverStatusService: ServerStatusService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer
  ) {
    this.title = 'MercaLink';
    this.matIconRegistry.addSvgIcon(
      'carteVitale-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/carte_vitale.svg')
    );
  }

  ngOnInit(): void {
    this.loginService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(u => this.isAdmin.set(hrIsAdmin(u)));
    combineLatest([this.isOnline$.pipe(distinctUntilChanged()), this.loginService.user$])
      .pipe(switchMap(([online, user]) => (online && user ? this.serverInfoService.version$() : of(null))), takeUntilDestroyed(this.destroyRef))
      .subscribe(v => this.serverVersion.set(v));
  }
}
